import { Flex, Icon, Text } from '@chakra-ui/react'
import lodash from 'lodash'
import React, { Fragment, useEffect, useState } from 'react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { textStyleKeys } from '~/themes/textStyles'
import {
  FilterItem,
  FilterRow,
  FilterSection,
  SelectedFilterData,
} from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

export type FilterItemClickedCallback = (data: SelectedFilterData) => void

interface TogglePillFilterItemProps<T> {
  item?: FilterItem<T>
  sectionTitle: string
  rowTitle?: string
  onClick?: FilterItemClickedCallback
}

export const TogglePillFilterItem = <T,>({
  item,
  onClick,
  rowTitle,
  sectionTitle,
}: TogglePillFilterItemProps<T>) => {
  const { title, isSelected, isDisabled, icon } = item as FilterItem<T>

  const handleItemClicked = () => {
    onClick?.({
      sectionTitle,
      rowTitle,
      item: item as FilterItem<T>,
      itemTitle: title,
    })
  }

  const textColor = isDisabled ? 'gray.700' : isSelected ? 'white' : 'gray.1300'

  return (
    <Flex
      py="0.25rem"
      px="1rem"
      alignItems="center"
      gap="0.5rem"
      borderRadius="6.25rem"
      bg={isSelected ? 'gray.1300' : 'gray.400'}
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      userSelect="none"
      onClick={isDisabled ? undefined : handleItemClicked}
    >
      {icon && (
        <Icon
          as={icon}
          sx={{ color: textColor, width: '1.25rem', height: '1.25rem' }}
        />
      )}
      <Text
        noOfLines={1}
        textStyle={textStyleKeys.Body1}
        sx={{ color: textColor }}
      >
        {title}
      </Text>
    </Flex>
  )
}

interface RowContainerProps<T> {
  row?: FilterRow<T>
  rowTitle?: string
  sectionTitle: string
  onFilterItemClicked?: FilterItemClickedCallback
}

export const RowContainer = <T,>({
  row,
  rowTitle,
  sectionTitle,
  onFilterItemClicked,
}: RowContainerProps<T>) => {
  const { title, items } = row as FilterRow<T>
  const hasTitle = Boolean(title)

  return (
    <Flex
      direction={['column', 'row']}
      alignItems="start"
      alignSelf="stretch"
      gap="0.75rem"
    >
      {hasTitle && (
        <Text
          textStyle={textStyleKeys.Body1}
          whiteSpace="nowrap"
          width={
            title?.toLowerCase() === 'office' ||
            title?.toLowerCase() === 'patient'
              ? '3.125rem'
              : 'default'
          }
        >
          {title}
        </Text>
      )}
      <Flex wrap="wrap" gap="0.75rem" flex="1">
        {items.map((item, index) => (
          <TogglePillFilterItem
            key={index}
            item={item}
            rowTitle={rowTitle}
            sectionTitle={sectionTitle}
            onClick={onFilterItemClicked}
          />
        ))}
      </Flex>
    </Flex>
  )
}

export const SectionHeader = ({
  title,
  sectionHovered = false,
  onClearClicked,
}: {
  title: string
  sectionHovered?: boolean
  onClearClicked?: () => void
}) => {
  return (
    <Flex
      data-testid="filter-section-header"
      height="1.75rem"
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      alignSelf="stretch"
    >
      <Text textStyle={textStyleKeys.Body2_Bold}>{title}</Text>
      {sectionHovered && (
        <MangoButton
          variant={MangoButtonVariant.Default_100}
          size={Size.sm}
          onClick={onClearClicked}
        >
          Clear
        </MangoButton>
      )}
    </Flex>
  )
}

interface SectionContainerProps<T> {
  section: FilterSection<T>
  onSectionFiltersCleared?: (sectionTitle: string) => void
  onFilterItemClicked?: FilterItemClickedCallback
}

export const SectionContainer = <T,>({
  section,
  onSectionFiltersCleared,
  onFilterItemClicked,
}: SectionContainerProps<T>) => {
  const [sectionHovered, setSectionHovered] = useState(false)
  const { title: sectionTitle, rows } = section

  const handleMouseEnter = () => setSectionHovered(true)
  const handleMouseLeave = () => setSectionHovered(false)

  const handleClearClicked = () => {
    onSectionFiltersCleared?.(sectionTitle)
  }

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      alignSelf="stretch"
      gap="1rem"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SectionHeader
        title={sectionTitle}
        sectionHovered={sectionHovered}
        onClearClicked={handleClearClicked}
      />
      {rows.map((row, index) => (
        <RowContainer
          key={index}
          row={row}
          rowTitle={row.title}
          sectionTitle={sectionTitle}
          onFilterItemClicked={onFilterItemClicked}
        />
      ))}
    </Flex>
  )
}

export interface FilterModalProps<T> {
  filterSections?: FilterSection<T>[]
  isOpen?: boolean
  onFiltersApplied?: (updatedFilters: FilterSection<T>[]) => void
  onClose?: () => void
}

export const FilterModal = <T,>({
  filterSections = [],
  isOpen,
  onFiltersApplied,
  onClose,
}: FilterModalProps<T>) => {
  const [localFilterSections, setLocalFilterSections] = useState<
    FilterSection<T>[]
  >([])

  useEffect(() => {
    if (isOpen) {
      // Deep copy to prevent mutations to the original data
      setLocalFilterSections(lodash.cloneDeep(filterSections))
    }
  }, [isOpen, filterSections])

  useKeyPresses({
    onPress: (e: KeyboardEvent) =>
      isOpen && e.key === 'Enter' && handleFiltersApplied(),
  })

  const handleFiltersApplied = () => {
    onFiltersApplied?.(localFilterSections)
    onClose?.()
  }

  const handleFilterItemClicked = (data: SelectedFilterData) => {
    const { sectionTitle, rowTitle, itemTitle } = data
    const updatedSections = [...localFilterSections]
    const section = updatedSections.find((s) => s.title === sectionTitle)
    if (!section) return

    const row = rowTitle
      ? section.rows.find((r) => r.title === rowTitle)
      : section.rows[0]
    if (!row) return

    const item = row.items.find((i) => i.title === itemTitle)
    if (!item) return

    item.isSelected = !item.isSelected

    // Handle special logic based on section title
    switch (sectionTitle) {
      // TODO add multi select logic so this doesn't have to be hardcoded
      case 'Outcome': {
        row.items.forEach((otherItem) => {
          if (otherItem !== item) {
            otherItem.isSelected = false
          }
        })
        break
      }

      case 'Duration': {
        const longerThanRow = section.rows.find(
          (row) => row.title === 'Longer Than',
        )
        const shorterThanRow = section.rows.find(
          (row) => row.title === 'Shorter Than',
        )

        const selectedLongerThanIndex = longerThanRow
          ? longerThanRow.items.findIndex((item) => item.isSelected)
          : -1
        const selectedShorterThanIndex = shorterThanRow
          ? shorterThanRow.items.findIndex((item) => item.isSelected)
          : -1

        // Disable conflicting shorter than items
        if (longerThanRow && shorterThanRow) {
          shorterThanRow.items.forEach((shorterItem, index) => {
            if (
              selectedLongerThanIndex >= index &&
              selectedLongerThanIndex !== -1
            ) {
              shorterItem.isDisabled = true
              shorterItem.isSelected = false
            } else {
              shorterItem.isDisabled = false
            }
          })

          // Disable conflicting longer than items
          longerThanRow.items.forEach((longerItem, index) => {
            if (
              selectedShorterThanIndex !== -1 &&
              selectedShorterThanIndex <= index
            ) {
              longerItem.isDisabled = true
              longerItem.isSelected = false
            } else {
              longerItem.isDisabled = false
            }
          })
        }
        break
      }
      default:
        break
    }

    setLocalFilterSections(updatedSections)
  }

  const handleSectionFiltersCleared = (sectionTitle: string) => {
    const updatedSections = localFilterSections.map((section) => {
      if (section.title === sectionTitle) {
        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            items: row.items.map((item) => ({
              ...item,
              isSelected: false,
              isDisabled: false,
            })),
          })),
        }
      }
      return section
    })
    setLocalFilterSections(updatedSections)
  }

  const handleAllFiltersCleared = () => {
    const resetSections = localFilterSections.map((section) => ({
      ...section,
      rows: section.rows.map((row) => ({
        ...row,
        items: row.items.map((item) => ({
          ...item,
          isSelected: false,
          isDisabled: false,
        })),
      })),
    }))
    setLocalFilterSections(resetSections)
  }

  return (
    <MangoModal
      size={Size.xl}
      isOpen={isOpen}
      onClose={onClose}
      header="Filters"
      body={
        <Flex direction="column" px="1.25rem" gap="1.5rem">
          {localFilterSections.map((section, index, array) => (
            <Fragment key={index}>
              <SectionContainer
                section={section}
                onSectionFiltersCleared={handleSectionFiltersCleared}
                onFilterItemClicked={handleFilterItemClicked}
              />
              {index < array.length - 1 && (
                <Flex
                  borderBottom="1px solid"
                  borderColor="gray.500"
                  width="full"
                />
              )}
            </Fragment>
          ))}
        </Flex>
      }
      footer={
        <>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={handleAllFiltersCleared}
          >
            Clear All
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={handleFiltersApplied}
          >
            Apply Filters
          </MangoButton>
        </>
      }
      scrollable
    />
  )
}
